import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  FileText,
  FileVideo,
  Image as ImageIcon,
  RotateCcw,
} from 'lucide-react';
import {
  AssistantUploadKind,
  AssistantUploadStatus,
} from '@xtreed/academy-shared';
import {
  isUploadSettled,
  type AssistantUploadView,
} from '@/hooks/assistant/useAssistantUploads';
import { retryUpload } from '@/lib/assistant-uploader';
import { notifyApiError } from '@/lib/api-message';
import { cn } from '@/lib/utils';
import { UploadStatusBadge } from './UploadStatusBadge';

const KIND_ICON = {
  [AssistantUploadKind.VIDEO]: FileVideo,
  [AssistantUploadKind.IMAGE]: ImageIcon,
  [AssistantUploadKind.MATERIAL]: FileText,
};

function UploadRow({ upload }: { upload: AssistantUploadView }) {
  const { t } = useTranslation('adminAssistant');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [retrying, setRetrying] = useState(false);
  const Icon = KIND_ICON[upload.kind] ?? FileText;
  const failed =
    upload.status === AssistantUploadStatus.FAILED ||
    Boolean(upload.local?.error);
  const progress =
    upload.status === AssistantUploadStatus.UPLOADING
      ? (upload.local?.progress ?? null)
      : null;

  const retry = async (file?: File) => {
    setRetrying(true);
    try {
      await retryUpload(upload.id, file);
    } catch (error) {
      notifyApiError(error, t('errors.upload', { name: upload.filename }));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <li className="flex flex-col gap-1 py-1.5">
      <div className="flex items-center gap-2 text-xs">
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-foreground">
          {upload.filename}
        </span>
        <UploadStatusBadge
          status={failed ? AssistantUploadStatus.FAILED : upload.status}
          progress={progress}
        />
      </div>
      {progress !== null && (
        <div className="ml-5 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}
      {(failed || upload.local?.needsFile) && (
        <div className="ml-5 flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void retry(file);
              event.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={retrying}
            onClick={() =>
              upload.local?.needsFile ? fileRef.current?.click() : void retry()
            }
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-ink hover:underline disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" />
            {upload.local?.needsFile
              ? t('uploads.pickAgain')
              : t('uploads.retry')}
          </button>
        </div>
      )}
    </li>
  );
}

interface UploadTrayProps {
  uploads: AssistantUploadView[];
}

export function UploadTray({ uploads }: UploadTrayProps) {
  const { t } = useTranslation('adminAssistant');
  const pending = uploads.filter((upload) => !isUploadSettled(upload)).length;
  const failed = uploads.filter(
    (upload) => upload.status === AssistantUploadStatus.FAILED,
  ).length;
  const [expanded, setExpanded] = useState(false);
  const open = expanded || failed > 0;

  if (uploads.length === 0) return null;

  return (
    <div className="shrink-0 border-t border-border/50 bg-card/60 px-3">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-2 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <span className="flex-1 text-left">
          {t('uploads.title')} · {uploads.length}
          {pending > 0 && ` · ${t('uploads.status.UPLOADING')} ${pending}`}
          {failed > 0 && (
            <span className="text-destructive">
              {' '}
              · {t('uploads.status.FAILED')} {failed}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <ul className="max-h-40 divide-y divide-border/30 overflow-y-auto pb-2">
          {uploads.map((upload) => (
            <UploadRow key={upload.id} upload={upload} />
          ))}
        </ul>
      )}
    </div>
  );
}
